import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms/Icon'
import { ExpandArrow } from '../../atoms/ExpandArrow'
import { useColumnDragResize } from './useColumnDragResize'
import { useHorizontalDragScroll } from './useHorizontalDragScroll'
import { useClassicScrollbars } from './useClassicScrollbars'
import { exportToExcel } from './exportUtils'
import {
  getStorageKeys,
  getStoredValue,
  storeValue,
  getOrderedVisibleColumns,
  getSortClass,
  getPaginationInfo,
  getPageNumbers,
  formatNumber,
} from './DataTable.utils'
import type { DataTableProps, DataTableColumn } from './DataTable.types'
import { MobileSortDropdown } from './MobileSortDropdown'
import { SwipeableCard } from './SwipeableCard'
import { PullToRefreshContainer } from './PullToRefreshContainer'
import { VirtualizedCardList } from './VirtualizedCardList'
import {
  DataTableContainer,
  DataTableHeader,
  DataTableActions,
  ColumnSettingsBtn,
  ColumnDropdown,
  ColumnDropdownHeader,
  ColumnDropdownHint,
  ColumnItem,
  ColumnCheckbox,
  ColumnLabel,
  TableWrapper,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  CheckboxCell,
  CheckboxCellHeader,
  CheckboxHeaderCell,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PaginationBtn,
  PaginationPages,
  PaginationPage,
  PaginationPerPage,
  PerPageLabel,
  PerPageSelect,
  BulkActionsBar,
  BulkSelectionInfo,
  BulkActions,
  BulkActionBtn,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  ResizeHandle,
  DragHandle,
  DragPreview,
  DropIndicator,
  DetailPanel,
  // Card view components
  CardViewContainer,
  DataCard,
  DataCardHeader,
  CardCheckbox,
  DataCardTitle,
  DataCardBody,
  DataCardRow,
  DataCardLabel,
  DataCardValue,
  DataCardFooter,
  DataCardExpandableContent,
  DataCardExpandableInner,
  DataCardExpandButton,
  TableViewWrapper,
  TableFoot,
} from './DataTable.styles'

export const DataTable = <T extends Record<string, any>>({
  // Data
  data,
  columns,
  ariaLabel,
  loading = false,
  error = null,
  emptyState,

  // Row actions
  onRowClick,
  onRowHover,
  rowActions,

  // Sorting
  sort = [],
  onSort,
  onSortChange,

  // Pagination
  pagination,

  // Selection
  selection,

  // Column management
  columnManagement,

  // Bulk actions
  bulkActions,

  // Export
  enableExport = false,
  exportConfig,
  exportColumns,
  onExcelExport,
  exportDisabled = false,
  exportDisabledTitle,

  // Detail view
  detailView,

  // Styling
  className,
  stickyHeader = false,
  bordered = false,
  hover = true,
  compact = false,
  dense = false,

  // Toolbar extras
  toolbarExtras,

  // Mobile view
  mobileView = 'cards',
  cardTitleColumn,
  mobileEnhancements,
  renderMobileCard: customRenderMobileCard,
}: DataTableProps<T>) => {
  const { t } = useTranslation('ui')
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const [selectedDetailItem, setSelectedDetailItem] = useState<T | null>(null)
  const [expandedCards, setExpandedCards] = useState<Set<string | number>>(new Set())
  const [swipingCardId, setSwipingCardId] = useState<string | number | null>(null)

  // Extract mobile enhancements with defaults
  const {
    mobileColumns,
    swipeActions,
    onRefresh,
    refreshing,
    estimatedCardHeight = 120,
    expandableCards = true,
    defaultExpanded = false,
  } = mobileEnhancements ?? {}

  // Horizontal drag scroll for table
  const tableWrapperRef = useHorizontalDragScroll()

  // Show the always-on styled scrollbar only when a real mouse is attached
  // (OS classic-scrollbar mode); trackpads fall back to native overlay bars.
  const classicScrollbars = useClassicScrollbars()

  // Get storage keys if column management is enabled
  const storageKeys = columnManagement?.storageKey
    ? getStorageKeys(columnManagement.storageKey)
    : null

  // Initialize column state from storage or defaults
  const [localColumnVisibility, setLocalColumnVisibility] = useState(() => {
    if (columnManagement) return columnManagement.visibility
    if (storageKeys) {
      return getStoredValue(
        storageKeys.visibility,
        columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
      )
    }
    return columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  })

  const [localColumnWidths, setLocalColumnWidths] = useState(() => {
    if (columnManagement) return columnManagement.widths
    if (storageKeys) return getStoredValue(storageKeys.widths, {})
    return {}
  })

  const [localColumnOrder, setLocalColumnOrder] = useState(() => {
    if (columnManagement) return columnManagement.order
    if (storageKeys) {
      return getStoredValue(
        storageKeys.order,
        columns.map((col) => col.key)
      )
    }
    return columns.map((col) => col.key)
  })

  // Get final column state (controlled or uncontrolled)
  const columnVisibility = columnManagement?.visibility ?? localColumnVisibility
  const columnWidths = columnManagement?.widths ?? localColumnWidths
  const columnOrder = columnManagement?.order ?? localColumnOrder

  // Get visible columns in order
  const visibleColumns = getOrderedVisibleColumns(
    columns,
    columnOrder,
    columnVisibility,
    columnWidths
  )

  // Determine mobile-specific columns
  const { priorityColumns, expandedOnlyColumns, hasExpandableContent } = useMemo(() => {
    // If mobileColumns is specified, use it to determine priority
    if (mobileColumns && mobileColumns.length > 0) {
      const priorityCols = mobileColumns
        .map((key) => visibleColumns.find((col) => col.key === key))
        .filter((col): col is DataTableColumn<T> => col !== undefined)
      const expandedCols = visibleColumns.filter((col) => !mobileColumns.includes(col.key))
      return {
        priorityColumns: priorityCols,
        expandedOnlyColumns: expandedCols,
        hasExpandableContent: expandedCols.length > 0 && expandableCards,
      }
    }

    // Default: first 4 columns as priority, rest as expandable
    const defaultPriorityCount = 4
    return {
      priorityColumns: visibleColumns.slice(0, defaultPriorityCount),
      expandedOnlyColumns: visibleColumns.slice(defaultPriorityCount),
      hasExpandableContent: visibleColumns.length > defaultPriorityCount && expandableCards,
    }
  }, [visibleColumns, mobileColumns, expandableCards])

  // Toggle card expansion
  const toggleCardExpanded = useCallback((id: string | number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  // Check if card is expanded
  const isCardExpanded = useCallback(
    (id: string | number) => {
      if (defaultExpanded) {
        // If default expanded, cards not in set are expanded
        return !expandedCards.has(id)
      }
      return expandedCards.has(id)
    },
    [expandedCards, defaultExpanded]
  )

  // Handle swipe start (to close other cards)
  const handleSwipeStart = useCallback((id: string | number) => {
    setSwipingCardId(id)
  }, [])

  // Reorder wrapper: converts visible column indices (from drag hook)
  // to correct key-based reorder in the full columnOrder array.
  // The hook sends slot indices (0..N where N = visibleColumns.length).
  // Slot i means "insert before visible column i"; slot N means "after last".
  const handleColumnReorder = useCallback(
    (dragIdx: number, dropSlot: number) => {
      const fromKey = visibleColumns[dragIdx]?.key
      if (!fromKey) return

      const currentOrder = [...columnOrder]
      const fromFullIdx = currentOrder.indexOf(fromKey)
      if (fromFullIdx === -1) return

      // Remove the dragged column
      currentOrder.splice(fromFullIdx, 1)

      if (dropSlot >= visibleColumns.length) {
        // Drop after the last visible column
        currentOrder.push(fromKey)
      } else {
        const toKey = visibleColumns[dropSlot]?.key
        if (!toKey) return
        // Find where the target column is now (after removal)
        const toFullIdx = currentOrder.indexOf(toKey)
        if (toFullIdx === -1) return
        // Insert before the target column
        currentOrder.splice(toFullIdx, 0, fromKey)
      }

      // Apply the new order
      if (columnManagement?.onReorder) {
        // Compute from/to in original full order for the consumer
        const origFromIdx = columnOrder.indexOf(fromKey)
        const newToIdx = currentOrder.indexOf(fromKey)
        columnManagement.onReorder(origFromIdx, newToIdx)
      } else {
        setLocalColumnOrder(currentOrder)
      }
    },
    [visibleColumns, columnOrder, columnManagement]
  )

  // Drag and resize handling
  const {
    dragState,
    dropTargetIndex,
    dragPreviewRef,
    handleDragStart,
    handleResizeStart,
    setHeaderRef,
  } = useColumnDragResize(
    handleColumnReorder,
    columnManagement?.onResize ??
      ((column, width) => {
        setLocalColumnWidths((prev) => ({ ...prev, [column]: width }))
      })
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        !target.closest('[data-column-dropdown]') &&
        !target.closest('[data-column-settings-btn]')
      ) {
        setColumnDropdownOpen(false)
      }
    }

    if (columnDropdownOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [columnDropdownOpen])

  // Save to localStorage when local state changes
  useEffect(() => {
    if (storageKeys && !columnManagement) {
      storeValue(storageKeys.visibility, localColumnVisibility)
    }
  }, [localColumnVisibility, storageKeys, columnManagement])

  useEffect(() => {
    if (storageKeys && !columnManagement) {
      storeValue(storageKeys.widths, localColumnWidths)
    }
  }, [localColumnWidths, storageKeys, columnManagement])

  useEffect(() => {
    if (storageKeys && !columnManagement) {
      storeValue(storageKeys.order, localColumnOrder)
    }
  }, [localColumnOrder, storageKeys, columnManagement])

  // Handle column visibility change
  const handleColumnVisibilityChange = useCallback(
    (column: string, visible: boolean) => {
      if (columnManagement?.onVisibilityChange) {
        columnManagement.onVisibilityChange(column, visible)
      } else {
        setLocalColumnVisibility((prev) => ({ ...prev, [column]: visible }))
      }
    },
    [columnManagement]
  )

  // Handle reset columns
  const handleResetColumns = useCallback(() => {
    if (columnManagement?.onReset) {
      columnManagement.onReset()
    } else {
      const defaultVisibility = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
      const defaultOrder = columns.map((col) => col.key)

      setLocalColumnVisibility(defaultVisibility)
      setLocalColumnWidths({})
      setLocalColumnOrder(defaultOrder)

      if (storageKeys) {
        window.localStorage.removeItem(storageKeys.visibility)
        window.localStorage.removeItem(storageKeys.widths)
        window.localStorage.removeItem(storageKeys.order)
      }
    }
  }, [columns, columnManagement, storageKeys])

  // Handle excel export
  const handleExcelExport = useCallback(() => {
    if (exportDisabled) return
    if (!selection) return

    const selectedItems = data.filter((item) =>
      selection.selectedRows.has(selection.getRowId(item))
    )

    if (selectedItems.length === 0) return

    // If parent component provides custom export handler, use it
    if (onExcelExport) {
      onExcelExport(selectedItems)
    } else if (exportColumns) {
      // Use exportColumns for export - convert to DataTableColumn format
      const columnsToUse = exportColumns.map((col) => ({
        key: col.key,
        label: col.label,
        exportFormatter: col.getValue,
      }))

      exportToExcel({
        data: selectedItems,
        columns: columnsToUse,
        filename: exportConfig?.filename,
      })
    } else {
      // Default implementation: export using display columns
      exportToExcel({
        data: selectedItems,
        columns: columns,
        filename: exportConfig?.filename,
        getExportData: exportConfig?.getExportData,
      })
    }

    // Clear selection after export
    selection.onSelectAll(false)
  }, [exportDisabled, data, selection, columns, exportColumns, exportConfig, onExcelExport])

  // Handle row click
  const handleRowClick = useCallback(
    (item: T) => {
      if (detailView) {
        setSelectedDetailItem(item)
      } else if (onRowClick) {
        onRowClick(item)
      }
    },
    [detailView, onRowClick]
  )

  // Render mobile card helper
  const renderMobileCard = useCallback(
    (item: T, rowId: string | number) => {
      const isSelected = selection?.selectedRows.has(rowId) ?? false
      const expanded = isCardExpanded(rowId)

      // Custom card renderer takes priority
      if (customRenderMobileCard) {
        const cardContent = (
          <div>
            {customRenderMobileCard({
              row: item,
              columns: visibleColumns,
              isExpanded: expanded,
              isSelected: isSelected,
              onToggleExpand: () => toggleCardExpanded(rowId),
              onToggleSelect: () => selection?.onRowSelect(rowId, !isSelected),
              onRowClick: () => handleRowClick(item),
            })}
          </div>
        )

        if (swipeActions) {
          return (
            <SwipeableCard
              item={item}
              swipeActions={swipeActions}
              onSwipeStart={() => handleSwipeStart(rowId)}
              forceClose={swipingCardId !== null && swipingCardId !== rowId}
            >
              {cardContent}
            </SwipeableCard>
          )
        }

        return cardContent
      }

      const titleColumn = cardTitleColumn || priorityColumns[0]?.key || visibleColumns[0]?.key || ''
      const titleValue = item[titleColumn] ?? '—'

      const cardContent = (
        <DataCard data-mobile-data-card $selected={isSelected} onClick={() => handleRowClick(item)}>
          <DataCardHeader>
            {selection && (
              <CardCheckbox>
                <input
                  type="checkbox"
                  aria-label={selection.selectRowLabel?.(item)}
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation()
                    selection.onRowSelect(rowId, e.target.checked)
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </CardCheckbox>
            )}
            <DataCardTitle>
              {priorityColumns[0]?.render
                ? priorityColumns[0].render(item, priorityColumns[0])
                : titleValue}
            </DataCardTitle>
          </DataCardHeader>

          {/* Priority columns (always visible) */}
          <DataCardBody>
            {priorityColumns.slice(1).map((column) => (
              <DataCardRow key={column.key}>
                <DataCardLabel>{column.label}</DataCardLabel>
                <DataCardValue>
                  {column.render ? column.render(item, column) : (item[column.key] ?? '—')}
                </DataCardValue>
              </DataCardRow>
            ))}
          </DataCardBody>

          {/* Expandable columns (hidden until expanded) */}
          {hasExpandableContent && (
            <DataCardExpandableContent $expanded={expanded}>
              <DataCardExpandableInner $expanded={expanded}>
                <div>
                  {expandedOnlyColumns.map((column) => (
                    <DataCardRow key={column.key}>
                      <DataCardLabel>{column.label}</DataCardLabel>
                      <DataCardValue>
                        {column.render ? column.render(item, column) : (item[column.key] ?? '—')}
                      </DataCardValue>
                    </DataCardRow>
                  ))}
                </div>
              </DataCardExpandableInner>
            </DataCardExpandableContent>
          )}

          {/* Expand/collapse button */}
          {hasExpandableContent && (
            <DataCardExpandButton
              onClick={(e) => {
                e.stopPropagation()
                toggleCardExpanded(rowId)
              }}
              aria-expanded={expanded}
            >
              <ExpandArrow $expanded={expanded} direction="down" />
              {expanded ? t('dataTable.mobile.showLess') : t('dataTable.mobile.showMore')}
            </DataCardExpandButton>
          )}

          {rowActions && (
            <DataCardFooter onClick={(e) => e.stopPropagation()}>{rowActions(item)}</DataCardFooter>
          )}
        </DataCard>
      )

      // Wrap with SwipeableCard if swipe actions are configured
      if (swipeActions) {
        return (
          <SwipeableCard
            item={item}
            swipeActions={swipeActions}
            onSwipeStart={() => handleSwipeStart(rowId)}
            forceClose={swipingCardId !== null && swipingCardId !== rowId}
          >
            {cardContent}
          </SwipeableCard>
        )
      }

      return cardContent
    },
    [
      customRenderMobileCard,
      selection,
      cardTitleColumn,
      priorityColumns,
      visibleColumns,
      handleRowClick,
      hasExpandableContent,
      expandedOnlyColumns,
      isCardExpanded,
      toggleCardExpanded,
      rowActions,
      swipeActions,
      handleSwipeStart,
      swipingCardId,
      t,
    ]
  )

  // Calculate pagination info
  const paginationInfo = pagination
    ? getPaginationInfo(pagination.currentPage, pagination.pageSize, pagination.total)
    : null

  // Render empty state
  if (!loading && data.length === 0) {
    const emptyContent = emptyState || (
      <EmptyState>
        <EmptyIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            width="48"
            height="48"
          >
            <path d="M12 2v20M2 12h20" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="9" strokeDasharray="4 2" />
          </svg>
        </EmptyIcon>
        <EmptyTitle>{t('dataTable.empty.title')}</EmptyTitle>
        <EmptyDescription>{t('dataTable.empty.description')}</EmptyDescription>
      </EmptyState>
    )

    return <DataTableContainer className={className}>{emptyContent}</DataTableContainer>
  }

  // Render error state
  if (error) {
    const errorContent = (
      <EmptyState>
        <EmptyIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            width="48"
            height="48"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </EmptyIcon>
        <EmptyTitle>{t('dataTable.error.title')}</EmptyTitle>
        <EmptyDescription>
          {typeof error === 'string' ? error : t('dataTable.error.description')}
        </EmptyDescription>
      </EmptyState>
    )

    return <DataTableContainer className={className}>{errorContent}</DataTableContainer>
  }

  return (
    <DataTableContainer className={className}>
      {/* Header with column settings */}
      <DataTableHeader>
        <DataTableActions>
          {toolbarExtras}
          <div style={{ position: 'relative' }}>
            <ColumnSettingsBtn
              data-column-settings-btn
              onClick={(e) => {
                e.stopPropagation()
                setColumnDropdownOpen(!columnDropdownOpen)
              }}
            >
              <Icon name="settings" size="small" />
              <span>{t('dataTable.columns.button')}</span>
            </ColumnSettingsBtn>

            <ColumnDropdown $active={columnDropdownOpen} data-column-dropdown>
              <ColumnDropdownHeader>{t('dataTable.columns.showHide')}</ColumnDropdownHeader>
              <ColumnDropdownHint>{t('dataTable.columns.dragHint')}</ColumnDropdownHint>
              {columns.map((column) => {
                // Non-hideable columns (e.g. the identity column) are locked
                // on: always checked, never toggleable.
                const locked = column.hideable === false
                return (
                  <ColumnItem key={column.key}>
                    <ColumnCheckbox
                      type="checkbox"
                      checked={
                        locked ||
                        (columnVisibility[column.key as keyof typeof columnVisibility] ?? true)
                      }
                      disabled={locked}
                      onChange={(e) => handleColumnVisibilityChange(column.key, e.target.checked)}
                    />
                    <ColumnLabel>{column.label}</ColumnLabel>
                  </ColumnItem>
                )
              })}
              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e5e5',
                }}
              >
                <button
                  onClick={handleResetColumns}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: '#f5f5f5',
                    border: '1px solid #e5e5e5',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#666',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e8e8e8'
                    e.currentTarget.style.color = '#333'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f5f5f5'
                    e.currentTarget.style.color = '#666'
                  }}
                >
                  {t('dataTable.columns.resetToDefault')}
                </button>
              </div>
            </ColumnDropdown>
          </div>
        </DataTableActions>
      </DataTableHeader>

      {/* Card View for Mobile (when mobileView='cards') */}
      {mobileView === 'cards' && (
        <CardViewContainer>
          {/* Mobile Sort Dropdown */}
          {onSort && (
            <MobileSortDropdown
              columns={columns}
              sort={sort}
              onSort={onSort}
              onSortChange={onSortChange}
            />
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>{t('dataTable.loading')}</div>
          ) : (
            <PullToRefreshContainer
              onRefresh={onRefresh}
              refreshing={refreshing}
              enabled={!!onRefresh}
            >
              <VirtualizedCardList
                data={data}
                getItemKey={(item) => selection?.getRowId(item) ?? item.id}
                estimatedItemHeight={estimatedCardHeight}
                expandedItems={expandedCards}
                renderCard={(item) => {
                  const rowId = selection?.getRowId(item) ?? item.id
                  return renderMobileCard(item, rowId)
                }}
              />
            </PullToRefreshContainer>
          )}
        </CardViewContainer>
      )}

      {/* Table View (hidden on mobile when mobileView='cards') */}
      <TableViewWrapper $hideOnMobile={mobileView === 'cards'}>
        <TableWrapper
          ref={tableWrapperRef}
          $stickyHeader={stickyHeader}
          $classicScrollbars={classicScrollbars}
        >
          <Table
            aria-label={ariaLabel}
            $bordered={bordered}
            $hover={hover}
            $compact={compact}
            $dense={dense}
          >
            <TableHead>
              <tr>
                {selection && (
                  <CheckboxHeaderCell>
                    <CheckboxCellHeader>
                      <input
                        type="checkbox"
                        aria-label={selection.selectAllLabel}
                        checked={selection.selectedRows.size === data.length && data.length > 0}
                        onChange={(e) => selection.onSelectAll(e.target.checked)}
                      />
                    </CheckboxCellHeader>
                  </CheckboxHeaderCell>
                )}
                {visibleColumns.map((column, index) => (
                  <TableHeader
                    key={column.key}
                    ref={setHeaderRef(index)}
                    data-column-index={index}
                    className={
                      column.sortable && onSort ? `sortable ${getSortClass(column.key, sort)}` : ''
                    }
                    onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth,
                      position: 'relative',
                      opacity: dragState.dragIndex === index ? 0.5 : 1,
                      paddingLeft: columnManagement ? '24px' : undefined,
                    }}
                    $hideOnMobile={column.hideOnMobile}
                  >
                    {columnManagement && (
                      <DragHandle
                        data-column-drag-handle
                        aria-label={t('dataTable.columns.moveColumn', {
                          column: column.label,
                        })}
                        title={t('dataTable.columns.moveColumn', {
                          column: column.label,
                        })}
                        onMouseDown={(e) => handleDragStart(index, e)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Icon name="grip-vertical" size="small" />
                      </DragHandle>
                    )}
                    {column.label}
                    {columnManagement && (
                      <ResizeHandle
                        onMouseDown={(e) => handleResizeStart(column.key, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    {/* Drop indicator on left edge of this column */}
                    {dragState.isDragging &&
                      dropTargetIndex === index &&
                      dropTargetIndex !== dragState.dragIndex &&
                      dropTargetIndex !== (dragState.dragIndex ?? -1) + 1 && (
                        <DropIndicator style={{ left: -1 }} />
                      )}
                    {/* Drop indicator on right edge of last column */}
                    {dragState.isDragging &&
                      index === visibleColumns.length - 1 &&
                      dropTargetIndex === visibleColumns.length &&
                      dropTargetIndex !== (dragState.dragIndex ?? -1) + 1 && (
                        <DropIndicator style={{ right: -1 }} />
                      )}
                  </TableHeader>
                ))}
                {rowActions && (
                  <TableHeader style={{ width: '100px' }}>{t('dataTable.actions')}</TableHeader>
                )}
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)}
                    style={{ textAlign: 'center', padding: '48px' }}
                  >
                    {t('dataTable.loading')}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item) => {
                  const rowId = selection?.getRowId(item) ?? item.id
                  const isSelected = selection?.selectedRows.has(rowId) ?? false

                  return (
                    <TableRow
                      key={rowId}
                      $selected={isSelected}
                      onClick={() => handleRowClick(item)}
                      onMouseEnter={() => onRowHover?.(item)}
                      onMouseLeave={() => onRowHover?.(null)}
                    >
                      {selection && (
                        <CheckboxCell>
                          <input
                            type="checkbox"
                            aria-label={selection.selectRowLabel?.(item)}
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation()
                              selection.onRowSelect(rowId, e.target.checked)
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </CheckboxCell>
                      )}

                      {visibleColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          $width={column.width}
                          $hideOnMobile={column.hideOnMobile}
                        >
                          {column.render ? column.render(item, column) : (item[column.key] ?? '—')}
                        </TableCell>
                      ))}

                      {rowActions && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {rowActions(item)}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
            <TableFoot>
              <tr>
                <td colSpan={visibleColumns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)} />
              </tr>
            </TableFoot>
          </Table>
        </TableWrapper>
      </TableViewWrapper>

      {/* Pagination */}
      {pagination && paginationInfo && (
        <PaginationContainer>
          <PaginationInfo>
            {t('dataTable.pagination.showing')}{' '}
            <strong>
              {paginationInfo.startItem}-{paginationInfo.endItem}
            </strong>{' '}
            {t('dataTable.pagination.of')}{' '}
            <strong>
              {pagination.formatTotal
                ? pagination.formatTotal(pagination.total)
                : formatNumber(pagination.total)}
            </strong>{' '}
            {t('dataTable.pagination.items')}
          </PaginationInfo>

          <PaginationControls>
            <PaginationBtn
              disabled={pagination.currentPage === 1}
              onClick={() => pagination.onPageChange(1)}
            >
              <Icon name="chevrons-left" size="small" />
            </PaginationBtn>
            <PaginationBtn
              disabled={pagination.currentPage === 1}
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
            >
              <Icon name="arrow-left" size="small" />
            </PaginationBtn>

            <PaginationPages>
              {getPageNumbers(pagination.currentPage, paginationInfo.totalPages).map((pageNum) => (
                <PaginationPage
                  key={pageNum}
                  $active={pageNum === pagination.currentPage}
                  onClick={() => pagination.onPageChange(pageNum)}
                >
                  {pageNum}
                </PaginationPage>
              ))}
            </PaginationPages>

            <PaginationBtn
              disabled={pagination.currentPage === paginationInfo.totalPages}
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
            >
              <Icon name="arrow-right" size="small" />
            </PaginationBtn>
            <PaginationBtn
              disabled={pagination.currentPage === paginationInfo.totalPages}
              onClick={() => pagination.onPageChange(paginationInfo.totalPages)}
            >
              <Icon name="chevrons-right" size="small" />
            </PaginationBtn>
          </PaginationControls>

          <PaginationPerPage>
            <PerPageLabel>{t('dataTable.pagination.show')}:</PerPageLabel>
            <PerPageSelect
              value={pagination.pageSize}
              onChange={(e) => pagination.onPageSizeChange(parseInt(e.target.value))}
            >
              {(pagination.pageSizeOptions || [10, 20, 50, 100]).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PerPageSelect>
          </PaginationPerPage>
        </PaginationContainer>
      )}

      {/* Bulk Actions Bar */}
      {selection && bulkActions && selection.selectedRows.size > 0 && (
        <BulkActionsBar>
          <BulkSelectionInfo>
            {t('dataTable.bulkActions.itemsSelected', {
              count: selection.selectedRows.size,
            })}
          </BulkSelectionInfo>
          <BulkActions>
            {enableExport && (
              <BulkActionBtn
                onClick={handleExcelExport}
                $variant="primary"
                disabled={exportDisabled}
                title={exportDisabled ? exportDisabledTitle : undefined}
              >
                <Icon name="download" size="small" />
                {t('dataTable.bulkActions.exportAsExcel')}
              </BulkActionBtn>
            )}
            {bulkActions.map((action, index) => (
              <BulkActionBtn
                key={index}
                onClick={() => {
                  if (action.disabled) return
                  const selectedItems = data.filter((item) =>
                    selection.selectedRows.has(selection.getRowId(item))
                  )
                  action.onClick(selectedItems)
                }}
                $variant={action.variant}
                disabled={action.disabled}
                title={action.title}
              >
                {action.icon}
                {action.label}
              </BulkActionBtn>
            ))}
          </BulkActions>
        </BulkActionsBar>
      )}

      {/* Drag preview */}
      {dragState.isDragging && dragState.dragIndex !== null && (
        <DragPreview ref={dragPreviewRef as any}>
          {visibleColumns[dragState.dragIndex]?.label}
        </DragPreview>
      )}

      {/* Detail Panel */}
      {detailView && (
        <DetailPanel $isOpen={selectedDetailItem !== null} $width={detailView.width}>
          {selectedDetailItem &&
            detailView.render(selectedDetailItem, () => setSelectedDetailItem(null))}
        </DetailPanel>
      )}
    </DataTableContainer>
  )
}
